/**
 * Página de gerenciamento de Inboxes e Instâncias do Tenant
 * 
 * Estrutura em duas tabs:
 * - Caixas de Entrada: Gerenciamento de inboxes (canais de comunicação)
 * - Instâncias: Gerenciamento de instâncias de provider (motores de mensagem)
 * 
 * As instâncias podem ser vinculadas/desvinculadas das inboxes conforme necessidade
 */
import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { 
  Plus, 
  MessageSquare, 
  Loader2, 
  Inbox, 
  CheckCircle, 
  BarChart3,
  AlertTriangle,
  MessageCircle,
  LayoutGrid,
  List,
  Crown,
  Package,
  PauseCircle,
  Server
} from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { usePermissions } from '@/hooks/usePermissions';
import { useResourceLimits } from '@/hooks/useResourceLimits';
import { useInboxes, type InboxData } from '@/hooks/useInboxes';
import { toast } from 'sonner';
import { logger } from '@/lib/logger';
import { InboxCard, ChannelType, InboxStatus } from '@/components/tenant/InboxCard';
import { InboxListRow } from '@/components/tenant/inbox/InboxListRow';
import { InboxDetailsModal } from '@/components/tenant/inbox/InboxDetailsModal';
import { CreateInboxDialog } from '@/components/tenant/CreateInboxDialog';
import { EditInboxDialog } from '@/components/tenant/EditInboxDialog';
import { QRCodeConnectionDialog } from '@/components/tenant/inbox/QRCodeConnectionDialog';
import { WhatsAppOAuthDialog } from '@/components/tenant/inbox/WhatsAppOAuthDialog';
import { MetaEmbeddedSignupDialog } from '@/components/tenant/inbox/MetaEmbeddedSignupDialog';
import { WebchatConfigDialog } from '@/components/tenant/webchat';
import { useInstances } from '@/hooks/useInstances';
import { InstanceList, LinkInstanceDialog, type InstanceListHandle } from '@/components/tenant/instance';
import { InboxGroupsTab } from '@/components/tenant/inbox/InboxGroupsTab';
import { SelectInstanceForInboxDialog } from '@/components/tenant/inbox/SelectInstanceForInboxDialog';
import { useInboxPageRealtime } from '@/hooks/useInboxPageRealtime';

import { cn } from '@/lib/utils';

// Tipo para tab ativa
type ActiveTab = 'inboxes' | 'instances' | 'groups';

// Tipo para modo de visualização
type ViewMode = 'cards' | 'list';

/**
 * Componente de métricas para KPI cards
 * Exibe estatísticas elegantes com ícones e variantes de cor
 */
/** Props do componente MetricCard */
interface MetricCardProps {
  title: string;
  value: string | number;
  icon: React.ElementType;
  variant: 'default' | 'success' | 'info' | 'whatsapp';
  subtitle?: string;
}

/** Mapeamento de variantes para cores (padrão DealListKpiCards) */
const variantStyles = {
  default: { cardBg: 'bg-purple-100 dark:bg-purple-900/30', iconColor: 'text-purple-700 dark:text-purple-300' },
  success: { cardBg: 'bg-lime-100 dark:bg-green-900/30', iconColor: 'text-green-700 dark:text-green-300' },
  info: { cardBg: 'bg-blue-100 dark:bg-blue-900/30', iconColor: 'text-blue-700 dark:text-blue-300' },
  whatsapp: { cardBg: 'bg-cyan-100 dark:bg-cyan-900/30', iconColor: 'text-cyan-700 dark:text-cyan-300' },
};

/** Card de KPI compacto horizontal (padrão unificado DealListKpiCards) */
const MetricCard = ({ title, value, icon: Icon, variant, subtitle }: MetricCardProps) => (
  <Card className={cn('border-0 shadow-none transition-all hover:shadow-md', variantStyles[variant].cardBg)}>
    <CardContent className="p-3">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <Icon className={cn('h-5 w-5 shrink-0', variantStyles[variant].iconColor)} />
          <p className="text-xs font-medium truncate text-gray-900 dark:text-gray-100">{title}</p>
        </div>
        <p className="text-xl font-bold leading-tight shrink-0 text-gray-900 dark:text-gray-100">{value}</p>
      </div>
      {subtitle && <p className="text-[10px] text-gray-600 dark:text-gray-400 mt-1 truncate">{subtitle}</p>}
    </CardContent>
  </Card>
);

/**
 * Componente de gerenciamento de caixas de entrada do tenant
 * Utiliza permissões granulares para controle de acesso:
 * - canCreate('inboxes'): criar novas caixas de entrada
 * - canEdit('inboxes'): editar, conectar e configurar webhooks
 * - canDelete('inboxes'): excluir caixas de entrada
 */
const TenantInboxes = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { currentTenant } = useAuth();
  const { canCreate, canEdit, canDelete } = usePermissions();
  
  // Hook de limites efetivos (plano base + add-ons)
  const { limits: resourceLimits, refetchLimits, isLoadingLimits } = useResourceLimits();
  
  // Realtime centralizado - 1 único canal para provider_instances + inboxes
  useInboxPageRealtime(currentTenant?.id);

  // Invalidação forçada ao voltar para a aba do navegador (ex.: retorno do
  // checkout Stripe após mudança de plano). Garante que a listagem de
  // instâncias e os limites do plano reflitam o novo estado sem exigir F5.
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState !== 'visible' || !currentTenant?.id) return;
      queryClient.invalidateQueries({ queryKey: ['instances', currentTenant.id] });
      queryClient.invalidateQueries({ queryKey: ['resource-limits', currentTenant.id] });
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [currentTenant?.id, queryClient]);


  
  
  // Hook para gerenciamento de instâncias (vincular/desvincular)
  const { 
    unlinkInstance, 
    isUnlinking,
    getInstanceByInboxId 
  } = useInstances(currentTenant?.id);
  
  // Hook com React Query + Supabase Realtime para inboxes
  const { data: inboxes = [], isLoading: loading, refetch } = useInboxes(currentTenant?.id);
  // Ref imperativa para disparar "Adicionar Instância" do botão da linha das abas.
  const instanceListRef = useRef<InstanceListHandle>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [inboxToEdit, setInboxToEdit] = useState<InboxData | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [inboxToDelete, setInboxToDelete] = useState<InboxData | null>(null);
  
  // Estado para impacto de exclusão (conversas/mensagens que serão deletadas)
  const [deletionImpact, setDeletionImpact] = useState<{
    conversation_count: number;
    message_count: number;
    has_campaigns: boolean;
    has_chat_groups: boolean;
    can_delete: boolean;
    block_reason: string | null;
  } | null>(null);
  const [loadingImpact, setLoadingImpact] = useState(false);
  const [connectDialogOpen, setConnectDialogOpen] = useState(false);
  const [inboxToConnect, setInboxToConnect] = useState<InboxData | null>(null);
  const [webchatConfigOpen, setWebchatConfigOpen] = useState(false);
  const [inboxToConfigureWebchat, setInboxToConfigureWebchat] = useState<InboxData | null>(null);
  
  // Estado para OAuth WhatsApp Oficial (NotificaMe/outros)
  const [oauthDialogOpen, setOauthDialogOpen] = useState(false);
  const [inboxToConnectOAuth, setInboxToConnectOAuth] = useState<InboxData | null>(null);
  const [oauthUrl, setOauthUrl] = useState<string>('');
  
  // Estado para Meta Embedded Signup Dialog (Meta WABA direto)
  const [metaSignupDialogOpen, setMetaSignupDialogOpen] = useState(false);
  const [inboxToConnectMeta, setInboxToConnectMeta] = useState<InboxData | null>(null);
  
  // Estado para vincular instância à inbox
  const [linkInstanceDialogOpen, setLinkInstanceDialogOpen] = useState(false);
  const [inboxToLinkInstance, setInboxToLinkInstance] = useState<InboxData | null>(null);

  // Estado do modal de detalhes da inbox (substitui a antiga página /tenant/inboxes/:id)
  const [searchParams, setSearchParams] = useSearchParams();
  const detailsInboxId = searchParams.get('inboxId');

  /** Abre o modal de detalhes mantendo deep link via query string */
  const handleViewDetails = (inbox: InboxData) => {
    const next = new URLSearchParams(searchParams);
    next.set('inboxId', inbox.id);
    setSearchParams(next, { replace: false });
  };

  /** Fecha o modal de detalhes limpando a query string */
  const handleCloseDetails = (open: boolean) => {
    if (open) return;
    const next = new URLSearchParams(searchParams);
    next.delete('inboxId');
    setSearchParams(next, { replace: true });
  };


  
  
  // Estado do modo de visualização com persistência no localStorage
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    const saved = localStorage.getItem('inboxes_viewMode');
    return (saved === 'cards' || saved === 'list') ? saved : 'cards';
  });

  // Estado da tab ativa com persistência no localStorage
  const [activeTab, setActiveTab] = useState<ActiveTab>(() => {
    const saved = localStorage.getItem('inboxes_activeTab');
    return (saved === 'inboxes' || saved === 'instances' || saved === 'groups') ? saved : 'inboxes';
  });

  /**
   * Altera a tab ativa e persiste no localStorage
   */
  const handleTabChange = (tab: string) => {
    const newTab = tab as ActiveTab;
    setActiveTab(newTab);
    localStorage.setItem('inboxes_activeTab', newTab);
  };

  /**
   * Altera o modo de visualização e persiste no localStorage
   */
  const handleViewModeChange = (mode: ViewMode) => {
    setViewMode(mode);
    localStorage.setItem('inboxes_viewMode', mode);
  };

  // NOTA: fetchInboxes foi substituído pelo hook useInboxes com Realtime
  // Chamadas anteriores a fetchInboxes() agora usam refetch()

  // NOTA: Inboxes podem ser criadas livremente. O controle de capacidade
  // é feito por instâncias (aba Instâncias), não por quantidade de inboxes.

  const handleEdit = (inbox: InboxData) => {
    setInboxToEdit(inbox);
    setEditDialogOpen(true);
  };

  const handleEditSuccess = () => {
    refetch();
    setEditDialogOpen(false);
    setInboxToEdit(null);
    // Invalidar cache de instâncias para sincronizar aba "Instâncias"
    queryClient.invalidateQueries({ queryKey: ['instances', currentTenant?.id] });
  };

  /**
   * Inicia processo de exclusão de inbox
   * Busca impacto (conversas/mensagens) antes de mostrar confirmação
   */
  const handleDelete = async (inbox: InboxData) => {
    setInboxToDelete(inbox);
    setDeleteDialogOpen(true);
    setLoadingImpact(true);
    setDeletionImpact(null);
    
    try {
      // Buscar impacto da exclusão via RPC
      const { data, error } = await supabase.rpc('get_inbox_deletion_impact', { 
        p_inbox_id: inbox.id 
      });
      
      if (error) {
        logger.error('Inboxes', 'Erro ao buscar impacto de exclusão', error);
        // Continua sem mostrar impacto, mas permite deletar
      } else if (data && data.length > 0) {
        setDeletionImpact({
          conversation_count: Number(data[0].conversation_count) || 0,
          message_count: Number(data[0].message_count) || 0,
          has_campaigns: data[0].has_campaigns || false,
          has_chat_groups: data[0].has_chat_groups || false,
          can_delete: data[0].can_delete ?? true,
          block_reason: data[0].block_reason || null,
        });
      }
    } catch (e) {
      logger.error('Inboxes', 'Exceção ao buscar impacto de exclusão', e);
    } finally {
      setLoadingImpact(false);
    }
  };

  /**
   * Confirma exclusão da inbox com atomicidade melhorada:
   * - PASSO 1: Buscar paths ANTES de deletar (dados ainda existem)
   * - PASSO 2: Excluir inbox PRIMEIRO (operação crítica - se falhar, nada alterado)
   * - PASSO 3: Limpar storage DEPOIS (arquivos órfãos são menos graves que perda de dados)
   */
  const confirmDelete = async () => {
    if (!inboxToDelete || !currentTenant?.id) return;

    try {
      // ============================================================
      // PASSO 1: Buscar paths ANTES de deletar (dados ainda existem)
      // ============================================================
      logger.info('Inboxes', 'Buscando arquivos do storage para limpeza', { 
        inboxId: inboxToDelete.id 
      });
      
      const { data: storagePaths, error: rpcError } = await supabase
        .rpc('cleanup_inbox_storage', { 
          p_inbox_id: inboxToDelete.id,
          p_tenant_hint: currentTenant.id 
        });
      
      if (rpcError) {
        logger.warn('Inboxes', 'Erro ao buscar paths do storage', rpcError);
      }

      // ============================================================
      // PASSO 1.5: Buscar IDs dos chat_groups vinculados a esta inbox
      // ============================================================
      const { data: chatGroups } = await supabase
        .from('chat_groups')
        .select('id')
        .eq('inbox_id', inboxToDelete.id);

      // ============================================================
      // PASSO 1.6: Remover conversations vinculadas aos chat_groups
      // (messages cascateiam via ON DELETE CASCADE)
      // ============================================================
      if (chatGroups && chatGroups.length > 0) {
        const groupIds = chatGroups.map(g => g.id);
        const { error: convsError } = await supabase
          .from('conversations')
          .delete()
          .in('group_id', groupIds);

        if (convsError) {
          logger.warn('Inboxes', 'Erro ao remover conversations vinculadas', convsError);
        }
      }

      // ============================================================
      // PASSO 2: Remover grupos de chat vinculados (participants cascateiam via ON DELETE CASCADE)
      // ============================================================
      const { error: chatGroupsError } = await supabase
        .from('chat_groups')
        .delete()
        .eq('inbox_id', inboxToDelete.id);

      if (chatGroupsError) {
        logger.warn('Inboxes', 'Erro ao remover grupos de chat vinculados', chatGroupsError);
        // Não bloqueia - tenta prosseguir com exclusão da inbox
      }

      // ============================================================
      // PASSO 3: Excluir inbox (operação crítica)
      // Se falhar aqui, grupos já foram removidos mas inbox permanece
      // ============================================================
      const { error } = await supabase
        .from('inboxes')
        .delete()
        .eq('id', inboxToDelete.id);

      if (error) throw error;

      // ============================================================
      // PASSO 4: Limpar storage (após sucesso do banco)
      // Se falhar aqui, apenas arquivos órfãos - menos crítico
      // ============================================================
      if (storagePaths && storagePaths.length > 0) {
        logger.info('Inboxes', 'Removendo arquivos do storage', { 
          totalFiles: storagePaths.length 
        });
        
        const BATCH_SIZE = 1000;
        let storageErrors = 0;
        
        for (let i = 0; i < storagePaths.length; i += BATCH_SIZE) {
          const batch = storagePaths.slice(i, i + BATCH_SIZE);
          const { error: storageError } = await supabase.storage
            .from('anexos')
            .remove(batch);
          
          if (storageError) {
            storageErrors++;
            logger.warn('Inboxes', 'Erro ao remover batch do storage', { 
              batchIndex: Math.floor(i / BATCH_SIZE), 
              error: storageError 
            });
          }
        }
        
        if (storageErrors > 0) {
          // Dados foram excluídos, mas alguns arquivos podem estar órfãos
          toast.warning(
            'Inbox excluída, mas alguns arquivos podem não ter sido removidos do storage'
          );
        } else {
          logger.info('Inboxes', 'Limpeza de storage concluída');
          toast.success('Inbox e arquivos excluídos com sucesso');
        }
      } else {
        toast.success('Inbox excluída com sucesso');
      }

      refetch();
      refetchLimits();
      
    } catch (error) {
      // Erro na exclusão do banco - nenhum arquivo foi deletado ainda
      logger.error('Inboxes', 'Erro ao excluir inbox', error);
      toast.error('Erro ao excluir inbox');
    } finally {
      setDeleteDialogOpen(false);
      setInboxToDelete(null);
    }
  };

  /**
   * Alterna status da inbox entre ativo/inativo
   * Ao DESATIVAR: tenta desconectar do provider primeiro (se WhatsApp não-oficial)
   * Se falhar a desconexão, prossegue com a inativação mesmo assim
   * NOTA: Inboxes suspensas não podem ser reativadas por esta função
   */
  const handleToggleStatus = async (inbox: InboxData) => {
    // Bloquear reativação se inbox está suspensa por downgrade
    if (inbox.status === 'suspended') {
      toast.warning('Esta inbox está suspensa por downgrade. Faça upgrade do seu plano para reativar.');
      navigate('/tenant/plans');
      return;
    }
    
    // Lógica alinhada com UI: apenas 'inactive' indica inbox inativa
    // Status 'pending' vem do painel admin e não deve ser alterado pelo usuário
    // Qualquer outro status (active, connecting, disconnected, error) = inbox ativa → desativar
    const newStatus = inbox.status === 'inactive' ? 'active' : 'inactive';
    
    // VALIDAÇÃO: Se está ATIVANDO, verificar se tem instância vinculada
    // Webchat não requer instância de provider
    if (newStatus === 'active' && inbox.channel_type !== 'webchat') {
      if (!inbox.linked_instance) {
        // Não tem instância vinculada - abrir modal para vincular
        toast.info('Para ativar esta inbox, é necessário vincular uma instância primeiro.');
        setInboxToLinkInstance(inbox);
        setLinkInstanceDialogOpen(true);
        return; // Interrompe a ativação - usuário deve vincular primeiro
      }
    }
    
    try {
      // PRIMEIRO: Atualizar status no banco ANTES de chamar provider
      // Isso evita race condition com webhooks do provider
      const { error } = await supabase
        .from('inboxes')
        .update({ status: newStatus })
        .eq('id', inbox.id);

      if (error) throw error;

      // Se DESATIVOU, liberar provider_instance via RPC OFICIAL e remover sessão remota
      // v1.3.0 (2026-04-21): troca fire-and-forget por await, com fallback para
      // inbox_credentials caso linked_instance não esteja populado no cache da UI.
      if (newStatus === 'inactive') {
        let oldSessionName: string | null = null;

        if (inbox.linked_instance) {
          const { data: unlinkData, error: unlinkError } = await supabase.rpc(
            'unlink_instance_from_inbox',
            {
              p_inbox_id: inbox.id,
              p_tenant_hint: currentTenant?.id,
            }
          );

          if (unlinkError) {
            // Log warning mas não falha - inbox já foi marcada como inactive
            logger.warn('Inboxes', 'Erro ao desvincular instância via RPC', unlinkError);
          } else {
            // v1.3.0 — captura session_name antiga para uso no delete_session.
            const result = unlinkData as
              | { old_session_name?: string | null; requires_remote_delete?: boolean }
              | null;
            if (result?.requires_remote_delete && result.old_session_name) {
              oldSessionName = result.old_session_name;
            }
            logger.info('Inboxes', 'Instância desvinculada via RPC unlink_instance_from_inbox', {
              has_old_session: !!oldSessionName,
            });
          }
        }

        // Fallback defensivo: se não obtivemos session_name pela RPC, busca direto
        // em inbox_credentials. Cobre casos onde linked_instance está ausente no
        // cache da UI mas o credentials ainda contém o session_name real do WAHA.
        if (inbox.channel_type === 'whatsapp' && !oldSessionName) {
          const { data: credRow } = await supabase
            .from('inbox_credentials')
            .select('credentials')
            .eq('inbox_id', inbox.id)
            .maybeSingle();
          const fallbackName = (credRow?.credentials as { session_name?: string } | null)?.session_name;
          if (fallbackName) {
            oldSessionName = fallbackName;
            logger.info('Inboxes', 'session_name capturado via fallback inbox_credentials (deactivate)');
          }
        }

        // Excluir sessão do servidor externo (AWAIT — garante execução, não bloqueia
        // por muito tempo: ~200-500ms). Erros são não-bloqueantes.
        if (inbox.channel_type === 'whatsapp' && oldSessionName) {
          logger.info('Inboxes', 'Solicitando delete_session ao provider-proxy (deactivate)', {
            session_name: oldSessionName,
          });
          try {
            const { data: deleteData } = await supabase.functions.invoke('provider-proxy', {
              body: {
                inbox_id: inbox.id,
                action: 'delete_session',
                session_name_override: oldSessionName,
              },
            });
            if (deleteData?.success) {
              logger.info('Inboxes', 'Sessão WAHA antiga removida com sucesso (deactivate)', {
                old_session_name: oldSessionName,
              });
            } else {
              logger.warn('Inboxes', 'Provider retornou erro ao excluir sessão (deactivate)', deleteData?.error);
            }
          } catch (deleteError) {
            // Ignora erro - inbox já está inativa
            logger.warn('Inboxes', 'Erro ao excluir sessão do provider (deactivate)', deleteError);
          }
        }
      }

      toast.success(`Inbox ${newStatus === 'active' ? 'ativada' : 'desativada'}`);
      refetch();
    } catch (error) {
      logger.error('Inboxes', 'Erro ao alterar status', error);
      toast.error('Erro ao alterar status da inbox');
    }
  };

  const handleCreateSuccess = () => {
    refetch();
    refetchLimits();
    // Invalidar cache de instâncias para sincronizar aba "Instâncias"
    queryClient.invalidateQueries({ queryKey: ['instances', currentTenant?.id] });
  };

  const handleConnect = (inbox: InboxData) => {
    setInboxToConnect(inbox);
    setConnectDialogOpen(true);
  };

  const handleConnected = () => {
    refetch();
    setConnectDialogOpen(false);
    setInboxToConnect(null);
  };

  /**
   * Ativa uma inbox que está inativa
   * Usado pelo QRCodeConnectionDialog quando detecta statusProtected
   * Retorna Promise para que o dialog possa aguardar a ativação
   */
  const handleActivateInboxFromDialog = async (): Promise<void> => {
    if (!inboxToConnect) {
      throw new Error('Nenhuma inbox selecionada para ativação');
    }

    // Verificar se precisa de instância vinculada primeiro
    if (inboxToConnect.channel_type !== 'webchat' && !inboxToConnect.linked_instance) {
      // Não tem instância vinculada - abrir modal para vincular
      toast.info('Para ativar esta inbox, é necessário vincular uma instância primeiro.');
      setInboxToLinkInstance(inboxToConnect);
      setLinkInstanceDialogOpen(true);
      setConnectDialogOpen(false);
      throw new Error('Instância não vinculada');
    }

    // Ativar a inbox
    const { error } = await supabase
      .from('inboxes')
      .update({ status: 'active' })
      .eq('id', inboxToConnect.id);

    if (error) {
      logger.error('Inboxes', 'Erro ao ativar inbox do dialog', error);
      throw error;
    }

    logger.info('Inboxes', 'Inbox ativada via dialog de conexão', { inboxId: inboxToConnect.id });
    refetch();
  };

  /**
   * Abre dialog de configuração do webchat
   */
  const handleConfigureWebchat = (inbox: InboxData) => {
    setInboxToConfigureWebchat(inbox);
    setWebchatConfigOpen(true);
  };

  /**
   * Abre dialog de conexão OAuth para WhatsApp
   * Para Meta WABA: Usa MetaEmbeddedSignupDialog (Facebook SDK)
   * Para outros providers (NotificaMe): Usa WhatsAppOAuthDialog com oauth_url
   */
  const handleConnectOAuth = async (inbox: InboxData) => {
    try {
      logger.info('Inboxes', 'Iniciando conexão OAuth', { 
        inboxId: inbox.id, 
        providerSlug: inbox.provider?.slug 
      });

      // VERIFICAÇÃO: Se for provider meta-whatsapp, usar MetaEmbeddedSignupDialog
      if (inbox.provider?.slug === 'meta-whatsapp') {
        logger.info('Inboxes', 'Provider Meta WABA detectado, abrindo Embedded Signup', { 
          inboxId: inbox.id 
        });
        setInboxToConnectMeta(inbox);
        setMetaSignupDialogOpen(true);
        return;
      }

      // Fluxo padrão para outros providers (NotificaMe, etc)
      // Busca oauth_url das credenciais da inbox
      const { data: credentials, error } = await supabase
        .from('inbox_credentials')
        .select('credentials')
        .eq('inbox_id', inbox.id)
        .single();

      if (error) {
        logger.error('Inboxes', 'Erro ao buscar credenciais', error);
        toast.error('Erro ao buscar configuração de conexão');
        return;
      }

      // Extrai oauth_url das credenciais
      const credentialsData = credentials?.credentials as Record<string, unknown> | null;
      const url = credentialsData?.oauth_url as string | undefined;

      if (!url) {
        logger.warn('Inboxes', 'URL OAuth não configurada para inbox', { inboxId: inbox.id });
        toast.error('URL de conexão não configurada. Contate o suporte.');
        return;
      }

      // Configura estados e abre dialog OAuth tradicional
      setInboxToConnectOAuth(inbox);
      setOauthUrl(url);
      setOauthDialogOpen(true);
    } catch (err) {
      logger.error('Inboxes', 'Erro ao iniciar conexão OAuth', err);
      toast.error('Erro ao iniciar conexão');
    }
  };

  /**
   * Callback chamado quando Meta Embedded Signup é concluído com sucesso
   * Atualiza a lista de inboxes para refletir novo status
   */
  const handleMetaSignupSuccess = () => {
    logger.info('Inboxes', 'Meta WABA conectado com sucesso via Embedded Signup', { 
      inboxId: inboxToConnectMeta?.id 
    });
    setMetaSignupDialogOpen(false);
    setInboxToConnectMeta(null);
    refetch();
  };

  /**
   * Callback chamado quando OAuth é concluído com sucesso
   * Atualiza status da inbox para pending_setup
   */
  const handleOAuthSuccess = async () => {
    if (!inboxToConnectOAuth) return;

    try {
      logger.info('Inboxes', 'OAuth concluído, atualizando status', { inboxId: inboxToConnectOAuth.id });

      // Atualiza status para pending_setup
      // Nota: Cast necessário pois o enum foi adicionado mas types.ts ainda não foi regenerado
      const { error } = await supabase
        .from('inboxes')
        .update({ status: 'pending_setup' as InboxStatus })
        .eq('id', inboxToConnectOAuth.id);

      if (error) {
        logger.error('Inboxes', 'Erro ao atualizar status da inbox', error);
        toast.error('Erro ao atualizar status');
        return;
      }

      toast.success('Conexão iniciada! Nossa equipe finalizará em até 24h.');
      refetch();
    } catch (err) {
      logger.error('Inboxes', 'Erro ao processar sucesso do OAuth', err);
      toast.error('Erro ao processar conexão');
    } finally {
      setOauthDialogOpen(false);
      setInboxToConnectOAuth(null);
      setOauthUrl('');
    }
  };

  /**
   * Handler para desvincular instância de uma inbox
   */
  const handleUnlinkInstance = async (inbox: InboxData) => {
    if (!inbox.linked_instance) {
      toast.error('Esta inbox não possui instância vinculada');
      return;
    }

    try {
      // CORREÇÃO: Passar inbox.id (não instance.id) pois a RPC espera p_inbox_id
      await unlinkInstance(inbox.id);
      // Toast já exibido pelo hook useInstances
      refetch();
    } catch (error) {
      // Erro já tratado no hook
    }
  };

  /**
   * Handler para abrir dialog de vincular instância
   */
  const handleLinkInstance = (inbox: InboxData) => {
    setInboxToLinkInstance(inbox);
    setLinkInstanceDialogOpen(true);
  };


  /**
   * Callback quando instância é vinculada com sucesso
   * Se a inbox estava sendo ativada, ativa automaticamente após vincular
   */
  const handleInstanceLinked = async () => {
    const inboxToActivate = inboxToLinkInstance;
    
    setLinkInstanceDialogOpen(false);
    setInboxToLinkInstance(null);
    refetch();
    // Invalidar cache de instâncias para sincronizar aba "Instâncias"
    queryClient.invalidateQueries({ queryKey: ['instances', currentTenant?.id] });
    toast.success('Instância vinculada com sucesso');
    
    // Se a inbox estava inativa (usuário tentou ativar), ativar automaticamente
    if (inboxToActivate && inboxToActivate.status !== 'active') {
      try {
        const { error } = await supabase
          .from('inboxes')
          .update({ status: 'active' })
          .eq('id', inboxToActivate.id);
        
        if (!error) {
          toast.success('Inbox ativada automaticamente');
          refetch();
        }
      } catch (e) {
        // Não bloqueia - usuário pode ativar manualmente
        logger.warn('Inboxes', 'Erro ao ativar inbox automaticamente', e);
      }
    }
  };

  /**
   * Verifica status da conexão usando provider-proxy (multi-provider)
   */
  const handleCheckStatus = async (inbox: InboxData) => {
    try {
      toast.info('Verificando status...');

      const { data, error } = await supabase.functions.invoke('provider-proxy', {
        body: {
          inbox_id: inbox.id,
          action: 'get_connection_status',
        },
      });

      if (error) {
        throw new Error(error.message || 'Erro ao verificar status');
      }

      if (!data?.success) {
        throw new Error(data?.error || 'Erro ao verificar status');
      }

      const result = data.data || data;

      if (result.connected && result.smartphoneConnected !== false) {
        toast.success('WhatsApp conectado!');
        if (result.webhooksConfigured) {
          toast.success('Webhooks configurados automaticamente!');
        }
      } else {
        toast.warning('WhatsApp não está conectado');
      }

      refetch();
    } catch (error) {
      console.error('Error checking status:', error);
      toast.error(error instanceof Error ? error.message : 'Erro ao verificar status');
    }
  };

  /**
   * Configura webhooks usando a função correta baseada no provider
   * TODO: Migrar para provider-proxy com action 'configure_webhooks' para unificar
   */
  const handleConfigureWebhooks = async (inbox: InboxData) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error('Sessão expirada. Faça login novamente.');
        return;
      }

      toast.info('Configurando webhooks...');

      // Determina função baseado no SLUG do provider (canônico, estável).
      // IMPORTANTE: usar slug em vez de display_name — display_name pode ser
      // customizado pelo admin (ex: "Inbox - S/ Botão Whatsapp" para WAHA) e
      // quebraria o roteamento, caindo no default z-api e gerando erro 400
      // "missing instance_id or token" em providers que não são z-api.
      const providerSlug = inbox.provider?.slug?.toLowerCase() || '';
      let functionName = 'z-api-configure-webhooks'; // default
      
      if (providerSlug === 'wuzapi') {
        functionName = 'wuzapi-configure-webhooks';
      } else if (providerSlug === 'waha') {
        functionName = 'waha-configure-webhooks';
      }
      // Adicionar outros providers conforme necessário

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/${functionName}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ inbox_id: inbox.id }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Erro ao configurar webhooks');
      }

      if (data.success) {
        toast.success('Webhooks configurados com sucesso!');
      } else {
        toast.warning(data.message || 'Alguns webhooks não foram configurados');
      }
    } catch (error) {
      console.error('Error configuring webhooks:', error);
      toast.error(error instanceof Error ? error.message : 'Erro ao configurar webhooks');
    }
  };

  /**
   * Desconecta a sessão WhatsApp do provider
   * A inbox permanece ativa no sistema e pode ser reconectada via QR Code
   */
  const handleDisconnect = async (inbox: InboxData) => {
    try {
      toast.info('Desconectando WhatsApp...');

      // v1.3.0 (2026-04-21): captura session_name ANTES do disconnect, garantindo
      // que tenhamos o nome correto para o delete_session mesmo que algo mude depois.
      let sessionNameForDelete: string | null = null;
      if (inbox.channel_type === 'whatsapp') {
        const { data: credRow } = await supabase
          .from('inbox_credentials')
          .select('credentials')
          .eq('inbox_id', inbox.id)
          .maybeSingle();
        sessionNameForDelete =
          (credRow?.credentials as { session_name?: string } | null)?.session_name ?? null;
      }

      const { data, error } = await supabase.functions.invoke('provider-proxy', {
        body: {
          inbox_id: inbox.id,
          action: 'disconnect',
        },
      });

      if (error) {
        throw new Error(error.message || 'Erro ao desconectar');
      }

      if (!data?.success) {
        throw new Error(data?.error || 'Erro ao desconectar');
      }

      // v1.3.0 (2026-04-21): após o logout, remover a sessão do servidor WAHA
      // usando AWAIT (em vez de fire-and-forget) — garante que a Promise não seja
      // descartada pelo runtime. Ao reconectar, wahaGetQRCode recria a sessão.
      if (inbox.channel_type === 'whatsapp') {
        logger.info('Inboxes', 'Solicitando delete_session ao provider-proxy (disconnect)', {
          has_session_name: !!sessionNameForDelete,
        });
        try {
          const { data: deleteData } = await supabase.functions.invoke('provider-proxy', {
            body: {
              inbox_id: inbox.id,
              action: 'delete_session',
              ...(sessionNameForDelete ? { session_name_override: sessionNameForDelete } : {}),
            },
          });
          if (deleteData?.success) {
            logger.info('Inboxes', 'Sessão WAHA removida com sucesso (disconnect)');
          } else {
            logger.warn('Inboxes', 'Provider retornou erro ao excluir sessão (disconnect)', deleteData?.error);
          }
        } catch (deleteError) {
          logger.warn('Inboxes', 'Erro ao excluir sessão do provider (disconnect)', deleteError);
        }
      }

      toast.success('WhatsApp desconectado! Para reconectar, escaneie o QR Code.');
      refetch();
    } catch (error) {
      console.error('Error disconnecting:', error);
      toast.error(error instanceof Error ? error.message : 'Erro ao desconectar');
    }
  };

  /**
   * Define a inbox como padrão para seu canal
   * A inbox padrão é preservada em caso de downgrade
   */
  const handleSetDefault = async (inbox: InboxData) => {
    if (!currentTenant?.id) return;
    
    try {
      const { data, error } = await supabase.rpc('set_default_inbox', {
        p_tenant_id: currentTenant.id,
        p_inbox_id: inbox.id
      });

      if (error) throw error;

      // Fazer cast para tipo esperado
      const result = data as { success: boolean; error?: string } | null;

      if (result?.success) {
        toast.success(`"${inbox.name}" definida como padrão`);
        refetch();
      } else {
        throw new Error(result?.error || 'Erro ao definir inbox padrão');
      }
    } catch (error) {
      logger.error('Inboxes', 'Erro ao definir inbox padrão', error);
      toast.error(error instanceof Error ? error.message : 'Erro ao definir inbox padrão');
    }
  };

  // Inboxes são livres - controle de capacidade é por instâncias

  // Estatísticas calculadas
  const activeCount = inboxes.filter(i => i.status === 'active').length;
  const whatsappCount = inboxes.filter(i => i.channel_type === 'whatsapp').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full min-w-0 w-full overflow-x-hidden">
      {/* Header Compacto - padrão Contatos */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 border-b">
        <div>
          <h1 className="text-xl font-semibold">Inboxes</h1>
          <p className="text-xs text-muted-foreground">
            Gerencie suas inboxes e instâncias
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {/* Botão Nova Inbox - exibido apenas na tab de inboxes */}
          {activeTab === 'inboxes' && canCreate('inboxes') && (
            <Button size="xs" onClick={() => setCreateDialogOpen(true)}>
              <Plus className="mr-1.5" />
              Nova Inbox
            </Button>
          )}
        </div>
      </div>

      {/* Tabs de Navegação - Caixas de Entrada / Instâncias / Grupos */}
      <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full min-w-0">
        <div className="px-4 flex items-center justify-between gap-3 mb-4 flex-wrap">
          <TabsList className="grid w-full max-w-lg grid-cols-3">
            <TabsTrigger value="inboxes" className="flex items-center gap-2">
              <Inbox className="h-4 w-4" />
              <span className="hidden sm:inline">Inboxes</span>
              <span className="sm:hidden">Inboxes</span>
              <Badge variant="secondary" className="ml-1 text-xs">
                {inboxes.length}
              </Badge>
            </TabsTrigger>
            <TabsTrigger value="instances" className="flex items-center gap-2">
              <Server className="h-4 w-4" />
              <span>Instâncias</span>
            </TabsTrigger>
            <TabsTrigger value="groups" className="flex items-center gap-2">
              <i className="icofont-tags" style={{ fontSize: '16px' }} />
              <span>Grupos</span>
            </TabsTrigger>
          </TabsList>

          {/* Botão "Adicionar Instância" — renderizado na linha das abas
              apenas quando a tab Instâncias está ativa. Dispara o fluxo
              via ref imperativa exposta por InstanceList. */}
          {activeTab === 'instances' && (
            <Button
              size="xs"
              onClick={() => instanceListRef.current?.triggerAdd()}
              className="transition-all hover:shadow-md hover:scale-[1.02]"
            >
              <Plus className="mr-1.5 h-4 w-4" />
              <span className="hidden sm:inline">Adicionar Instância</span>
              <span className="sm:hidden">Adicionar</span>
            </Button>
          )}
        </div>



        {/* ========== TAB: CAIXAS DE ENTRADA ========== */}
        <TabsContent value="inboxes" className="space-y-4 sm:space-y-6 mt-0 px-4 min-w-0">
          {/* KPI Cards - Responsivo */}
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 py-1 min-w-0">

            <MetricCard 
              title="Total" 
              value={inboxes.length} 
              icon={Inbox} 
              variant="default" 
            />
            <MetricCard 
              title="Ativos" 
              value={activeCount} 
              icon={CheckCircle} 
              variant="success"
              subtitle="Funcionando"
            />
            <MetricCard 
              title="WhatsApp" 
              value={whatsappCount} 
              icon={MessageCircle} 
              variant="whatsapp"
              subtitle="Conectados"
            />
          </div>


          {/* Barra de Ferramentas - Toggle de Visualização - Responsivo */}
          {inboxes.length > 0 && (
            <div className="flex items-center justify-between gap-2 flex-wrap min-w-0">
              <p className="text-xs sm:text-sm text-muted-foreground">
                {inboxes.length} {inboxes.length === 1 ? 'inbox' : 'inboxes'}
              </p>
              
              {/* Toggle Cards/Lista - Lista oculta em mobile muito pequeno */}
              <div className="flex items-center bg-muted/50 rounded-lg p-1 border border-border">
                <button
                  onClick={() => handleViewModeChange('cards')}
                  className={cn(
                    'flex items-center gap-2 px-2 sm:px-3 py-1.5 rounded-md text-sm font-medium transition-all',
                    viewMode === 'cards'
                      ? 'bg-foreground text-background shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  )}
                >
                  <LayoutGrid className="h-4 w-4" />
                </button>
                <button
                  onClick={() => handleViewModeChange('list')}
                  className={cn(
                    'hidden sm:flex items-center gap-2 px-2 sm:px-3 py-1.5 rounded-md text-sm font-medium transition-all',
                    viewMode === 'list'
                      ? 'bg-foreground text-background shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  )}
                >
                  <List className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}

          {/* Lista de Inboxes ou Empty State - Responsivo */}
          {inboxes.length === 0 ? (
            <div className="relative overflow-hidden rounded-2xl border-2 border-dashed border-muted-foreground/20 bg-gradient-to-br from-muted/30 to-muted/10 p-6 sm:p-12">
              {/* Efeito decorativo */}
              <div className="absolute top-0 right-0 w-40 h-40 bg-primary/5 rounded-full blur-3xl" />
              
              <div className="relative flex flex-col items-center justify-center text-center">
                <div className="w-14 h-14 sm:w-20 sm:h-20 rounded-2xl bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center mb-4 sm:mb-6">
                  <MessageSquare className="h-7 w-7 sm:h-10 sm:w-10 text-primary/60" />
                </div>
                
                <h3 className="text-lg sm:text-xl font-semibold text-foreground mb-2">
                  Nenhum canal configurado
                </h3>
                <p className="text-sm sm:text-base text-muted-foreground mb-4 sm:mb-6 max-w-md">
                  <span className="hidden sm:inline">Conecte um canal de comunicação como WhatsApp, Instagram ou Messenger para começar a receber mensagens.</span>
                  <span className="sm:hidden">Conecte WhatsApp, Instagram ou Messenger.</span>
                </p>
                
                {/* Configurar Primeiro - permissão de criar */}
                {canCreate('inboxes') && (
                  <Button 
                    size="xs"
                    onClick={() => setCreateDialogOpen(true)}
                    className="transition-all hover:shadow-md hover:scale-[1.02]"
                  >
                    <Plus className="mr-1.5 h-4 w-4" />
                    <span className="hidden sm:inline">Configurar Primeiro Canal</span>
                    <span className="sm:hidden">Configurar Canal</span>
                  </Button>
                )}
              </div>
            </div>
          ) : viewMode === 'cards' ? (
            // Visualização em Cards (Grid)
            <div className="grid grid-cols-1 gap-3 sm:gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 min-w-0">
              {inboxes.map((inbox) => {
                return (
                  <InboxCard
                    key={inbox.id}
                    inbox={inbox}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    onToggleStatus={handleToggleStatus}
                    onConnect={handleConnect}
                    onCheckStatus={handleCheckStatus}
                    onConfigureWebhooks={handleConfigureWebhooks}
                    onDisconnect={handleDisconnect}
                    onSetDefault={handleSetDefault}
                    onConfigureWebchat={handleConfigureWebchat}
                    onConnectOAuth={handleConnectOAuth}
                    onUnlinkInstance={handleUnlinkInstance}
                    onLinkInstance={handleLinkInstance}
                    onViewDetails={handleViewDetails}
                    canEditInbox={canEdit('inboxes')}
                    canDeleteInbox={canDelete('inboxes')}
                  />
                );
              })}
            </div>
          ) : (
            // Visualização em Lista
            <Card className="w-full min-w-0 overflow-hidden">
              <CardContent className="p-0 overflow-x-auto">

                {/* Header da tabela - títulos das colunas (oculto em mobile) */}
                <div className="hidden sm:flex items-center gap-4 px-4 py-2 border-b bg-muted/30 text-xs font-medium text-muted-foreground">
                  <div className="w-8 shrink-0" /> {/* Ícone do canal */}
                  <div className="flex-1 min-w-0">Inbox</div>
                  <div className="hidden sm:flex w-24 shrink-0">Cód. Inbox</div>
                  <div className="hidden sm:flex w-40 shrink-0">Identificador</div>
                  <div className="hidden md:flex w-32 shrink-0">Instância</div>
                  <div className="hidden md:flex w-32 shrink-0">Provider</div>
                  <div className="hidden lg:flex w-16 shrink-0">Versão</div>
                  <div className="w-28 shrink-0">Status</div>
                  <div className="w-8 shrink-0" /> {/* Menu de ações */}
                </div>

                {/* Lista de inboxes */}
                <div className="divide-y divide-border">
                  {inboxes.map((inbox) => {
                    return (
                      <InboxListRow
                        key={inbox.id}
                        inbox={inbox}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                        onToggleStatus={handleToggleStatus}
                        onConnect={handleConnect}
                        onCheckStatus={handleCheckStatus}
                        onConfigureWebhooks={handleConfigureWebhooks}
                        onDisconnect={handleDisconnect}
                        onSetDefault={handleSetDefault}
                        onConfigureWebchat={handleConfigureWebchat}
                        onConnectOAuth={handleConnectOAuth}
                        onUnlinkInstance={handleUnlinkInstance}
                        onLinkInstance={handleLinkInstance}
                        onViewDetails={handleViewDetails}
                        canEditInbox={canEdit('inboxes')}
                        canDeleteInbox={canDelete('inboxes')}
                      />
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ========== TAB: INSTÂNCIAS ========== */}
        <TabsContent value="instances" className="mt-0 px-4 min-w-0">
          {currentTenant && (
            <InstanceList ref={instanceListRef} tenantId={currentTenant.id} />
          )}
        </TabsContent>

        {/* ========== TAB: GRUPOS ========== */}
        <TabsContent value="groups" className="mt-0 px-4 min-w-0">
          <InboxGroupsTab />

        </TabsContent>
      </Tabs>

      {/* Create Inbox Dialog - Always mounted to preserve state */}
      <CreateInboxDialog
        open={createDialogOpen && !!currentTenant}
        onOpenChange={setCreateDialogOpen}
        tenantId={currentTenant?.id ?? ''}
        onSuccess={handleCreateSuccess}
      />

      {/* Delete Confirmation Dialog - com detalhes de impacto */}
      <AlertDialog 
        open={deleteDialogOpen} 
        onOpenChange={(open) => {
          setDeleteDialogOpen(open);
          if (!open) {
            setDeletionImpact(null);
            setInboxToDelete(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" />
              Excluir inbox?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4">
                <p>
                  Essa ação <strong className="text-foreground">não pode ser desfeita</strong>. A inbox 
                  <strong className="text-foreground"> "{inboxToDelete?.name}"</strong> será permanentemente excluída.
                </p>
                
                {/* Loading state */}
                {loadingImpact && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Calculando impacto...
                  </div>
                )}
                
                {/* Impacto da exclusão */}
                {!loadingImpact && deletionImpact && (
                  <div className="rounded-lg border border-destructive/20 bg-destructive/5 p-4 space-y-3">
                    <p className="font-medium text-destructive text-sm">
                      Os seguintes dados serão excluídos permanentemente:
                    </p>
                    <ul className="text-sm space-y-1 ml-4 list-disc text-foreground">
                      <li>
                        <strong>{deletionImpact.conversation_count.toLocaleString('pt-BR')}</strong> {deletionImpact.conversation_count === 1 ? 'conversa' : 'conversas'}
                      </li>
                      <li>
                        <strong>{deletionImpact.message_count.toLocaleString('pt-BR')}</strong> {deletionImpact.message_count === 1 ? 'mensagem' : 'mensagens'}
                      </li>
                    </ul>
                    
                    {/* Aviso de bloqueio */}
                    {!deletionImpact.can_delete && (
                      <div className="pt-2 border-t border-destructive/20 mt-2">
                        <p className="text-sm text-amber-600 dark:text-amber-400 flex items-center gap-1.5">
                          <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                          <span>Exclusão bloqueada:</span>
                        </p>
                        <p className="text-xs text-muted-foreground mt-1 ml-5">
                          {deletionImpact.block_reason}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={loadingImpact || (deletionImpact && !deletionImpact.can_delete)}
            >
              {loadingImpact ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                  Verificando...
                </>
              ) : (
                'Excluir Permanentemente'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit Inbox Dialog */}
      <EditInboxDialog
        open={editDialogOpen && !!inboxToEdit}
        onOpenChange={setEditDialogOpen}
        inboxId={inboxToEdit?.id ?? ''}
        onSuccess={handleEditSuccess}
      />

      {/* QR Code Connection Dialog - Always mounted to preserve state */}
      <QRCodeConnectionDialog
        open={connectDialogOpen && !!inboxToConnect}
        onOpenChange={setConnectDialogOpen}
        inboxId={inboxToConnect?.id ?? ''}
        inboxName={inboxToConnect?.name ?? ''}
        inboxIdentifier={inboxToConnect?.identifier}
        onConnected={handleConnected}
        providerSlug={inboxToConnect?.provider?.slug}
        onActivateInbox={handleActivateInboxFromDialog}
      />

      {/* WebChat Config Dialog */}
      {currentTenant && (
        <WebchatConfigDialog
          open={webchatConfigOpen && !!inboxToConfigureWebchat}
          onOpenChange={setWebchatConfigOpen}
          inboxId={inboxToConfigureWebchat?.id ?? ''}
          tenantId={currentTenant.id}
          inboxName={inboxToConfigureWebchat?.name ?? ''}
        />
      )}

      {/* WhatsApp OAuth Dialog - Conexão OAuth para WhatsApp Oficial (NotificaMe/outros) */}
      <WhatsAppOAuthDialog
        open={oauthDialogOpen && !!inboxToConnectOAuth}
        onOpenChange={setOauthDialogOpen}
        inboxId={inboxToConnectOAuth?.id ?? ''}
        inboxName={inboxToConnectOAuth?.name ?? ''}
        oauthUrl={oauthUrl}
        onSuccess={handleOAuthSuccess}
      />

      {/* Meta Embedded Signup Dialog - Conexão direta com Meta WABA */}
      {currentTenant && (
        <MetaEmbeddedSignupDialog
          open={metaSignupDialogOpen && !!inboxToConnectMeta}
          onOpenChange={setMetaSignupDialogOpen}
          inboxId={inboxToConnectMeta?.id ?? ''}
          inboxName={inboxToConnectMeta?.name ?? ''}
          onSuccess={handleMetaSignupSuccess}
        />
      )}

      {/* Dialog para vincular instância à inbox */}
      {currentTenant && (
        <SelectInstanceForInboxDialog
          open={linkInstanceDialogOpen}
          onOpenChange={setLinkInstanceDialogOpen}
          inboxId={inboxToLinkInstance?.id ?? ''}
          inboxName={inboxToLinkInstance?.name ?? ''}
          channelType={inboxToLinkInstance?.channel_type ?? ''}
          providerSlug={inboxToLinkInstance?.provider?.slug}
          tenantId={currentTenant.id}
          onSuccess={handleInstanceLinked}
        />
      )}

      {/* Modal de detalhes da inbox (substitui a antiga página de detalhes) */}
      <InboxDetailsModal
        open={!!detailsInboxId}
        onOpenChange={handleCloseDetails}
        inboxId={detailsInboxId}
      />



    </div>
  );
};

export default TenantInboxes;
